import { Image } from '@common/components/Image';
import { FontFamily } from '@common/theming/Fonts';
import { Palette } from '@common/theming/Palette';
import React, { CSSProperties } from 'react';
import { BRANCH_APP_LINK, DESKTOP_CONTENT_WIDTH } from '../../constants';
import { PortfolioAsset } from '../../types';
import { TextBlock } from '../TextBlock';
import { Features, Highlights, Map, TopEmployers } from './';

type AssetProps = {
  asset: PortfolioAsset;
  showImageGallery: () => void;
};

const Section: React.FC<{ children: React.ReactNode; bg?: string; marginBottom?: CSSProperties['marginBottom'] }> = ({
  bg = Palette.WHITE,
  children,
  marginBottom = 50,
}) => {
  return (
    <div
      style={{
        alignItems: 'center',
        backgroundColor: bg,
        display: 'flex',
        justifyContent: 'center',
        marginBottom,
        width: '100%',
      }}>
      {children}
    </div>
  );
};

const Content: React.FC<{
  children: React.ReactNode;
  bg?: string;
  alignItems?: CSSProperties['alignItems'];
  padding?: CSSProperties['padding'];
}> = ({ bg = Palette.WHITE, children, alignItems = 'center', padding = 0 }) => {
  return (
    <div
      style={{
        alignItems,
        backgroundColor: bg,
        display: 'flex',
        flexDirection: 'column',
        padding,
        textAlign: 'left',
        width: DESKTOP_CONTENT_WIDTH,
      }}>
      {children}
    </div>
  );
};

export const Asset: React.FC<AssetProps> = ({ asset, showImageGallery }) => (
  <div style={{ backgroundColor: Palette.WHITE, paddingBottom: 50 }}>
    {/* Header */}
    <Section marginBottom={0}>
      <Content padding={30}>
        <Image image="logo-black" width={100} />
      </Content>
    </Section>
    {/* Hero */}
    <Section bg={Palette.LIGHT_GRAY}>
      <Content bg={Palette.LIGHT_GRAY} padding={50}>
        <div style={{ textAlign: 'center' }}>
          <TextBlock fontFamily={FontFamily.DM_SERIF_DISPLAY_REGULAR} size={36} color={Palette.BLACK}>
            {asset.header.title}
          </TextBlock>
        </div>
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <TextBlock size={16} color={Palette.BLACK}>
            {asset.header.locationName}
          </TextBlock>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }}>
          <div
            onClick={() => {
              showImageGallery();
            }}
            style={{
              backgroundImage: `url(${asset.header.headerImage.file.url})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              cursor: 'pointer',
              height: 251,
              width: DESKTOP_CONTENT_WIDTH,
            }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginRight: asset.images.length > 3 ? 5 : 0,
                marginTop: 5,
              }}>
              <div style={{ backgroundColor: 'rgba(0,0,0,0.20)', display: 'flex', padding: 5 }}>
                {asset.images.slice(0, 3).map((image, i) => {
                  return (
                    <div
                      key={`image-preview${i}`}
                      style={{ border: `1px solid ${Palette.WHITE}`, marginRight: asset.images.length > 3 ? 5 : 0 }}>
                      <img src={image.file.url} style={{ display: 'flex', height: 32, width: 38 }} />
                    </div>
                  );
                })}
                {asset.images.length > 3 ? (
                  <div
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      height: 32,
                      justifyContent: 'center',
                      width: 38,
                    }}>
                    <TextBlock>{`+${asset.images.length - 3}`}</TextBlock>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }}>
          <Highlights highlights={asset.assetHighlights}></Highlights>
        </div>
      </Content>
    </Section>
    {/* About */}
    <Section>
      <Content alignItems="flex-start">
        <TextBlock style={{ textAlign: 'left' }} size={24} color={Palette.BLACK}>
          About this asset
        </TextBlock>
        <TextBlock style={{ marginTop: 12, textAlign: 'left' }} size={16} color={Palette.BLACK}>
          {asset.description}
        </TextBlock>
      </Content>
    </Section>
    {asset.assetFeatures && asset.topEmployers ? (
      <Section>
        <Content>
          <div
            style={{
              display: 'grid',
              gridGap: 20,
              gridTemplateColumns: '1fr 1fr',
            }}>
            {asset.assetFeatures && <Features asset={asset} />}
            {asset.topEmployers && <TopEmployers asset={asset} />}
          </div>
        </Content>
      </Section>
    ) : asset.assetFeatures ? (
      <Section>
        <Content alignItems={'flex-start'}>
          <div
            style={{
              width: '75%',
            }}>
            {asset.assetFeatures && <Features asset={asset} />}
          </div>
        </Content>
      </Section>
    ) : (
      <Section>
        <Content alignItems={'flex-start'}>
          <div
            style={{
              width: '75%',
            }}>
            {asset.assetFeatures && <Features asset={asset} />}
          </div>
        </Content>
      </Section>
    )}
    {/* Marketing Text  */}
    <Section>
      <Content alignItems={'flex-start'}>
        {asset.marketingText?.map((text, i) => (
          <div key={`marketing-text-${i}`}>
            {text.heading && (
              <TextBlock style={{ marginBottom: 20 }} size={24} color={Palette.BLACK}>
                {text.heading}
              </TextBlock>
            )}
            {text.marketingParagraph && (
              <TextBlock style={{ marginBottom: 20 }} size={16} color={Palette.BLACK}>
                {text.marketingParagraph}
              </TextBlock>
            )}
            {text.additionalParagraph1 && (
              <TextBlock style={{ marginBottom: 20 }} size={16} color={Palette.BLACK}>
                {text.additionalParagraph1}
              </TextBlock>
            )}
            {text.additionalParagraph2 && (
              <TextBlock style={{ marginBottom: 20 }} size={16} color={Palette.BLACK}>
                {text.additionalParagraph2}
              </TextBlock>
            )}
          </div>
        ))}
      </Content>
    </Section>
    {/* Map */}
    <Section>
      <Content>
        <Map asset={asset} />
      </Content>
    </Section>
    {/* Cta */}
    <Section marginBottom={0}>
      <Content>
        <div
          style={{
            backgroundColor: Palette.LIGHT_GRAY,
            border: `1px solid #E1E1E1`,
            borderRadius: 15,
            paddingTop: 50,
          }}>
          <img
            style={{ height: 200, marginBottom: 30, objectFit: 'none', width: DESKTOP_CONTENT_WIDTH }}
            src="/assets/gfx/AssetMarketingGraphic.svg"
          />
          <TextBlock
            style={{ marginTop: -40, textAlign: 'center' }}
            color={Palette.BLACK}
            size={30}
            fontFamily={FontFamily.DM_SERIF_DISPLAY_REGULAR}>
            Add to your portfolio
          </TextBlock>
          <TextBlock
            fontFamily={FontFamily.NEUE_HAAS_DISPLAY_THIN}
            style={{ marginTop: 20, textAlign: 'center' }}
            color={Palette.BLACK}
            size={16}>
            Build your wealth with real estate
          </TextBlock>
          <TextBlock
            fontFamily={FontFamily.NEUE_HAAS_DISPLAY_THIN}
            style={{ textAlign: 'center' }}
            color={Palette.BLACK}
            size={16}>
            investing in a hands-off way
          </TextBlock>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 55,
              marginTop: 36,
              width: DESKTOP_CONTENT_WIDTH,
            }}>
            <button
              className="ctaButton"
              onClick={() => {
                window.open(asset.ctaUrl ?? BRANCH_APP_LINK, '_blank');
              }}
              style={{
                alignItems: 'center',
                backgroundColor: Palette.BLUE,
                border: 0,
                borderRadius: 10,
                cursor: 'pointer',
                display: 'flex',
                height: 60,
                justifyContent: 'center',
                width: 235,
              }}>
              <TextBlock size={16}>Invest Now</TextBlock>
            </button>
          </div>
        </div>
      </Content>
    </Section>
  </div>
);
